import {
  groupParticipantAvatar,
  groupRoster,
  isGroupConversation,
} from '../groupParticipant.js';
import {
  aiReplyDisplayName,
  conversationAttendantIdentity,
  isUnattributedWameMessage,
} from '../messageAuthor.js';

export const isForwardedAudio = contentAttributes =>
  !!contentAttributes?.forwarded;

export const getAudioAppearance = ({ contentAttributes, isOutgoing }) => {
  if (isForwardedAudio(contentAttributes)) return 'forwarded';
  return isOutgoing ? 'outgoing' : 'incoming';
};

export const getAudioAvatarIdentity = ({
  contentAttributes,
  sender,
  inbox,
  conversation,
  isOutgoing,
  groupSenderName = '',
}) => {
  const aiDisplayName = aiReplyDisplayName({
    contentAttributes,
    senderType: sender?.type,
    senderName: sender?.name,
  });
  if (aiDisplayName) return { name: aiDisplayName, src: '' };

  // Em grupo o avatar é de QUEM GRAVOU, não do grupo. O nome chega pronto de
  // quem já decidiu o rótulo da bolha (getAudioGroupSenderName), para que foto
  // e legenda não possam discordar sobre quem é a pessoa; aqui só se resolve a
  // imagem. Sem foto, o `src` vazio faz o Avatar desenhar as iniciais desse
  // mesmo nome — que era o objetivo: nunca mais a cara do grupo numa fala
  // individual.
  //
  // Só na RECEBIDA: `groupParticipantAvatar` lê o participante de
  // content_attributes, que só existe no que chega do grupo. Na mensagem de
  // saída não há participante nenhum, então deixar este ramo capturá-la (agora
  // que o rótulo também vale na saída) devolveria `src` vazio e o atendente
  // perderia a própria foto no player — o ramo `Channel::Api && isOutgoing`
  // logo abaixo é quem sabe resolvê-la.
  if (groupSenderName && !isOutgoing) {
    return {
      name: groupSenderName,
      src: groupParticipantAvatar(contentAttributes, groupRoster(conversation)),
    };
  }

  if (inbox?.channel_type === 'Channel::Api' && isOutgoing) {
    // Quem ENVIOU vem antes de quem e responsavel. Este ramo devolvia o
    // responsavel da conversa em toda mensagem de saida, entao um audio
    // gravado por um atendente passava a exibir outro assim que o lead trocava
    // de dono — a mesma alternancia de nome que o balao de texto ja tinha
    // corrigido, sobrevivendo dentro do player.
    if (
      !isUnattributedWameMessage({
        contentAttributes,
        senderType: sender?.type,
        inbox,
      }) &&
      sender?.name
    ) {
      return {
        name: sender.name,
        src: sender.thumbnail || sender.avatar_url || sender.avatarUrl || '',
      };
    }

    // Sem autor identificavel (audio gravado no celular ligado a conexao, ou
    // payload reduzido sem `sender`): assina o atendente responsavel pelo
    // lead, igual ao balao e ao avatar. Nunca o nome da instancia.
    const attendant = conversationAttendantIdentity(conversation);
    if (attendant) return attendant;
  }

  // Recebidas: quando o sender não vem junto da mensagem (ex.: payload
  // reduzido do menu de contexto), cai no contato da conversa.
  const contact = !isOutgoing ? conversation?.meta?.sender : null;
  const resolved =
    sender?.thumbnail || sender?.avatar_url || sender?.avatarUrl
      ? sender
      : contact || sender;

  return {
    name: resolved?.name || sender?.name || '',
    src:
      resolved?.thumbnail || resolved?.avatar_url || resolved?.avatarUrl || '',
  };
};

/**
 * Nome de quem gravou o áudio, exibido dentro do cartão do player.
 *
 * Só existe em conversa de GRUPO, nos dois sentidos. É a mesma regra que
 * a figurinha e a foto já seguiam pelo BaseBubble — o áudio ficou de fora
 * porque desliga o rótulo do Base (`hide-sender-name`), já que a bolha dele é
 * transparente e o nome sairia solto sobre o fundo da conversa.
 *
 * O `displaySenderName` chega pronto de useDisplaySenderName: a resolução de
 * QUEM é o autor não pode ser refeita aqui. Reimplementá-la foi exatamente o
 * que fez o álbum de figurinhas exibir o dono do token admin no lugar do
 * participante. Este helper decide apenas SE o nome aparece.
 *
 * @param {object} params
 * @param {string} params.displaySenderName  nome já resolvido pela bolha
 * @param {object} params.conversation       conversa da mensagem
 * @returns {string} nome a exibir, ou '' quando o rótulo não se aplica
 */
export const getAudioGroupSenderName = ({
  displaySenderName,
  conversation,
}) => {
  // O rótulo vale nos DOIS sentidos. Enquanto valia só na recebida, o áudio
  // gravado por um atendente saía sem assinatura no grupo, enquanto o texto
  // dele ao lado aparecia assinado — e num grupo, onde várias pessoas do mesmo
  // canal falam, era impossível saber quem gravou.
  //
  // `displaySenderName` já chega resolvido de useDisplaySenderName, que trata
  // os dois sentidos e devolve '' quando o nome do vendedor deve ficar oculto.
  // Este helper continua decidindo apenas SE o nome aparece; refazer a
  // resolução aqui foi o que fez o álbum de figurinhas exibir o dono do token
  // admin no lugar do participante.
  if (!isGroupConversation(conversation)) return '';
  return displaySenderName || '';
};
